/**
 * Per-run eval variables (gh #3007).
 *
 * A scenario may declare `variables: Record<string, unknown[]>` — a map of
 * author-provided arrays. When the scenario runs `runs: N` times, run *i*
 * (1-based) resolves `{{var(NAME)}}` against `variables.NAME[i-1]`, so a
 * data-mutating eval can lease a disjoint fixture per run instead of colliding
 * on one shared seed row. The value at an index may be an object, so one index
 * yields a coherent bundle (`{{var(case).patient_id}}`, `{{var(case).slot_id}}`
 * move together). Resolution is done ONCE per run over the whole scenario
 * snapshot, so the prompt (`input`/`history`) and the assertion (`expected`/
 * `evaluator_instructions`) always see the same value.
 *
 * Seeding/reset of the underlying fixtures stays agent-managed — this module is
 * only the templating + per-run indexing primitive.
 */

import { z } from 'zod';
import { resolvePlaceholdersDeep, type PlaceholderContext } from './scheduling/placeholders';
import { SUPPORTED_LOCALES, isSupportedLocale, type SupportedLocale } from './constants';
import { timestampSchema } from './contracts/common';
import { isIsoTimestamp } from './time';

/** Whether `Intl` knows `timezone`: the date context's check, and its constructor's fallback test. */
function isIanaTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(0);
    return true;
  } catch {
    return false;
  }
}

/**
 * A session's date context: the instant, timezone and locale its runs resolve `{{now()}}`
 * against. Declared here, with its constructor and guard, rather than in the evals contract
 * (which re-exports it): `@wayai/core`'s root re-exports this module, and every Worker isolate
 * loads that root, so the contract's other schemas would load with it (gh #5083, decision 61).
 */
export const evalDateContextSchema = z.object({
  base_time: timestampSchema.refine(isIsoTimestamp, 'Expected a canonical ISO timestamp'),
  timezone: z.string().min(1).refine(isIanaTimezone, 'Expected an IANA timezone'),
  locale: z.enum(SUPPORTED_LOCALES),
}).strict();

export type EvalDateContext = z.infer<typeof evalDateContextSchema>;

/** Per-run resolution touches `{{var()}}` across the whole snapshot. */
const VAR_ONLY_FUNCTIONS: ReadonlySet<string> = new Set(['var']);

/** Session-relative resolution is limited to the existing `{{now()}}` surface. */
const EVAL_DATE_FUNCTIONS: ReadonlySet<string> = new Set(['now']);

/** Scenario-level `variables:` — a map of author-provided arrays. */
export type EvalScenarioVariables = Record<string, unknown[]>;

/** A variable array that is too short to cover the scenario's run count. */
export interface InsufficientEvalVariable {
  variable: string;
  array_length: number;
  required_runs: number;
}

/** True when `value` is a usable scenario-variables map (non-empty, all arrays). */
export function hasEvalVariables(value: unknown): value is EvalScenarioVariables {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const entries = Object.entries(value as Record<string, unknown>);
  return entries.length > 0 && entries.every(([, v]) => Array.isArray(v));
}

/**
 * Validate that every declared variable array covers `numberOfRuns` (run i reads
 * index i-1, so an array must have at least `numberOfRuns` entries). Returns the
 * offending arrays; empty when all cover the run count. Callers reject rather
 * than wrap/modulo — a short array is an authoring error, and silently reusing a
 * fixture across runs would re-introduce the self-collision this feature removes.
 */
export function findInsufficientEvalVariables(
  variables: EvalScenarioVariables,
  numberOfRuns: number,
): InsufficientEvalVariable[] {
  const out: InsufficientEvalVariable[] = [];
  for (const [name, arr] of Object.entries(variables)) {
    if (arr.length < numberOfRuns) {
      out.push({ variable: name, array_length: arr.length, required_runs: numberOfRuns });
    }
  }
  return out;
}

/**
 * Build the index-free row for run at 0-based `index`:
 * `{ NAME: variables.NAME[index] }` for every declared variable. Out-of-range
 * indices yield `undefined` (→ `{{var(NAME)}}` resolves empty); callers should
 * validate with `findInsufficientEvalVariables` first.
 */
export function evalVariablesRow(
  variables: EvalScenarioVariables,
  index: number,
): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const [name, arr] of Object.entries(variables)) {
    row[name] = arr[index];
  }
  return row;
}

/** Build an immutable session date context, falling back to UTC. */
export function createEvalDateContext(
  baseTime: string,
  timezone: string | null | undefined,
  locale: string | null | undefined,
): EvalDateContext {
  if (!isIsoTimestamp(baseTime)) {
    throw new Error('Eval date context requires a canonical ISO base time');
  }

  const candidateTimezone = timezone?.trim() || 'UTC';
  const resolvedTimezone = isIanaTimezone(candidateTimezone) ? candidateTimezone : 'UTC';

  const resolvedLocale: SupportedLocale = locale && isSupportedLocale(locale) ? locale : 'en';
  return evalDateContextSchema.parse({
    base_time: baseTime,
    timezone: resolvedTimezone,
    locale: resolvedLocale,
  });
}

/** Validate a stored session date context. */
export function isEvalDateContext(value: unknown): value is EvalDateContext {
  return evalDateContextSchema.safeParse(value).success;
}

/**
 * Resolve `{{var(...)}}` (and `{{var(...).path}}`) in every string leaf of a
 * scenario snapshot for run at 0-based `index`. Non-string leaves and
 * non-`var` placeholders pass through untouched.
 */
export function resolveEvalScenarioSnapshot<T>(
  snapshot: T,
  variables: EvalScenarioVariables,
  index: number = 0,
): T {
  return resolvePlaceholdersDeep(
    snapshot,
    { variables: evalVariablesRow(variables, index) },
    { only: VAR_ONLY_FUNCTIONS },
  );
}

/** Resolve `{{now()}}` only in eval input and expected fields. */
export function resolveEvalScenarioDates<T>(snapshot: T, dateContext: EvalDateContext): T {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return snapshot;
  }

  const record = snapshot as Record<string, unknown>;
  const out = { ...record };
  const runtimeContext: PlaceholderContext = {
    base_time: dateContext.base_time,
    timezone: dateContext.timezone,
    locale: dateContext.locale,
  };
  if ('message_text' in record) {
    out.message_text = resolvePlaceholdersDeep(record.message_text, runtimeContext, { only: EVAL_DATE_FUNCTIONS });
  }
  if ('message_expected_response' in record) {
    out.message_expected_response = resolvePlaceholdersDeep(
      record.message_expected_response,
      runtimeContext,
      { only: EVAL_DATE_FUNCTIONS },
    );
  }
  return out as T;
}
